'use client';
import React from 'react';
import { RotateCcw, LogOut, Shield, AlertTriangle, TrendingUp, TrendingDown } from 'lucide-react';
import type { Player, MoveRecord } from './MatchRoomContent';

interface Props {
  winner: 'p1' | 'p2' | 'draw';
  scores: { p1: number; p2: number };
  self: Player;
  opponent: Player;
  moveHistory: MoveRecord[];
  resultRecorded: boolean;
  entryFee: number;
  prizePool: number;
  platformFee: number;
  onRematch: () => void;
  onExit: () => void;
}

export default function MatchResultModal({
  winner, scores, self, opponent, moveHistory, resultRecorded,
  entryFee, prizePool, platformFee, onRematch, onExit
}: Props) {
  const isWin = winner === 'p1';
  const isDraw = winner === 'draw';
  const payout = isWin ? prizePool : isDraw ? entryFee : 0;
  const ratingChange = isWin ? +18 : isDraw ? 0 : -12;

  const p1Moves = moveHistory.filter(m => m.player === 'p1').length;
  const p2Moves = moveHistory.filter(m => m.player === 'p2').length;
  const p1Acc = p1Moves > 0 ? Math.round((moveHistory.filter(m => m.player === 'p1' && m.valid).length / p1Moves) * 100) : 100;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(13,20,36,0.92)', backdropFilter: 'blur(8px)' }}>
      <div className="w-full max-w-md rounded-2xl overflow-hidden" style={{ background: '#0D1424', border: '1px solid rgba(255,255,255,0.1)' }}>
        {/* Result header */}
        <div className="relative px-6 py-8 text-center overflow-hidden"
          style={{ background: isWin ? 'linear-gradient(135deg, rgba(16,185,129,0.2), rgba(124,58,237,0.2))' : isDraw ? 'linear-gradient(135deg, rgba(245,158,11,0.2), rgba(124,58,237,0.2))' : 'linear-gradient(135deg, rgba(239,68,68,0.2), rgba(124,58,237,0.2))' }}>
          <div className="text-5xl mb-3">{isWin ? '🏆' : isDraw ? '🤝' : '😔'}</div>
          <h2 className="text-2xl font-black mb-1" style={{ color: isWin ? '#10B981' : isDraw ? '#F59E0B' : '#EF4444' }}>
            {isWin ? 'Victory!' : isDraw ? 'Draw!' : 'Defeat'}
          </h2>
          <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
            Final Score: <strong style={{ color: 'var(--foreground)' }}>{scores.p1} – {scores.p2}</strong>
          </p>

          {/* Server result badge */}
          <div className="flex items-center justify-center gap-2 mt-3">
            {resultRecorded ? (
              <span className="flex items-center gap-1.5 text-xs px-3 py-1 rounded-full" style={{ background: 'rgba(16,185,129,0.15)', color: '#10B981', border: '1px solid rgba(16,185,129,0.3)' }}>
                <Shield size={12} /> Result recorded server-side
              </span>
            ) : (
              <span className="flex items-center gap-1.5 text-xs px-3 py-1 rounded-full animate-pulse" style={{ background: 'rgba(245,158,11,0.15)', color: '#F59E0B' }}>
                <AlertTriangle size={12} /> Recording result…
              </span>
            )}
          </div>
        </div>

        {/* Stats grid */}
        <div className="px-6 py-4 border-b" style={{ borderColor: 'var(--border)' }}>
          <div className="grid grid-cols-3 gap-3 text-center">
            <StatCell label="Moves" value={String(p1Moves)} />
            <StatCell label="Accuracy" value={`${p1Acc}%`} />
            <StatCell label="Rating" value={`${ratingChange >= 0 ? '+' : ''}${ratingChange}`}
              valueColor={ratingChange > 0 ? '#10B981' : ratingChange < 0 ? '#EF4444' : '#F59E0B'}
              icon={ratingChange > 0 ? <TrendingUp size={12} /> : ratingChange < 0 ? <TrendingDown size={12} /> : undefined} />
          </div>
        </div>

        {/* Financial summary */}
        <div className="px-6 py-4 border-b" style={{ borderColor: 'var(--border)' }}>
          <p className="text-xs font-semibold uppercase tracking-widest mb-3" style={{ color: 'var(--muted-foreground)' }}>Match Summary</p>
          <div className="space-y-2">
            <FinRow label="Entry Fee" value={`-$${entryFee.toFixed(2)}`} color="#EF4444" />
            <FinRow label="Platform Fee (10%)" value={`-$${platformFee.toFixed(2)}`} color="var(--muted-foreground)" />
            <FinRow label="Prize Pool" value={`$${prizePool.toFixed(2)}`} color="#10B981" />
            <div className="border-t pt-2 mt-2" style={{ borderColor: 'var(--border)' }}>
              <FinRow label={isWin ? 'You Receive' : isDraw ? 'Refunded' : 'Net Result'} value={`${payout > 0 ? '+' : ''}$${(payout - entryFee).toFixed(2)}`}
                color={payout > entryFee ? '#10B981' : payout === entryFee ? '#F59E0B' : '#EF4444'} bold />
            </div>
          </div>
          <p className="text-xs mt-2" style={{ color: 'var(--muted-foreground)', opacity: 0.6 }}>
            ✓ All results validated server-side. Client scores not trusted.
          </p>
        </div>

        {/* Actions */}
        <div className="px-6 py-4 flex gap-3">
          <button onClick={onRematch}
            className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold transition-all hover:scale-105"
            style={{ background: 'linear-gradient(135deg, #7C3AED, #5B21B6)', color: '#fff' }}>
            <RotateCcw size={15} /> Rematch
          </button>
          <button onClick={onExit}
            className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold transition-all hover:opacity-80"
            style={{ background: 'rgba(255,255,255,0.07)', color: 'var(--foreground)', border: '1px solid rgba(255,255,255,0.1)' }}>
            <LogOut size={15} /> Exit
          </button>
        </div>
      </div>
    </div>
  );
}

function StatCell({ label, value, valueColor, icon }: { label: string; value: string; valueColor?: string; icon?: React.ReactNode }) {
  return (
    <div className="rounded-xl p-3" style={{ background: 'rgba(255,255,255,0.04)' }}>
      <div className="flex items-center justify-center gap-1 mb-1" style={{ color: valueColor || 'var(--foreground)' }}>
        {icon}
        <span className="text-lg font-black">{value}</span>
      </div>
      <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{label}</p>
    </div>
  );
}

function FinRow({ label, value, color, bold }: { label: string; value: string; color: string; bold?: boolean }) {
  return (
    <div className="flex justify-between items-center">
      <span className="text-sm" style={{ color: 'var(--muted-foreground)', fontWeight: bold ? 600 : 400 }}>{label}</span>
      <span className="text-sm font-bold" style={{ color }}>{value}</span>
    </div>
  );
}
